// src/components/common/BulkDeleteControls.tsx
/**
 * The select-and-delete affordance shared by the four admin queues.
 *
 * The flow the care office asked for: an icon puts the table into selection mode and says so, the
 * delete button stays disabled until something is selected, and confirming requires typing a word.
 *
 * ⚠️ **Gated three ways**, and all three must pass: the caller must be an `ActsCareAdmin`, the module
 * setting must allow it, and the SDK must actually expose a delete method. When the last is missing the
 * button renders **disabled with a reason** rather than vanishing — a feature that is built but waiting
 * on an endpoint should look pending, not absent, or it gets rebuilt by someone else later.
 */
import { Alert, Badge, Button, Group, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconChecklist, IconTrash, IconX } from '@tabler/icons-react';
import { AccessControl } from '@/components';
import TypeToConfirmModal from './TypeToConfirmModal';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { BulkDeleteOutcome, UseBulkDeleteResult } from '@/hooks/useBulkDelete';

export interface BulkDeleteControlsProps {
  bulk: UseBulkDeleteResult;
  /** "intakes", "requests", "cases", "registrations" — used in the copy. */
  entityLabel: string;
  /** The word the admin must type, from the module settings. */
  confirmWord: string;
  /** False when the module setting is off — the whole control is then hidden. */
  allowed: boolean;
  /**
   * Whether the server keeps the record after deleting it.
   *
   * ⚠️ **All four AC queues are soft-deleted** (`isDeleted = true`) as of the 2026-08-26 backend
   * change — registrations and cases were hard deletes until then — so every caller in this app passes
   * this. It stays a prop, defaulting to the stronger wording, because a future queue elsewhere may not
   * be recoverable and over-warning is the safer way to be wrong. Do not set it without checking:
   * "cannot be retrieved" said of a recoverable record teaches admins that the warning overstates
   * things, and they stop reading it.
   */
  recoverable?: boolean;
  /** Extra line in the confirmation, e.g. that approved records in the selection will be skipped. */
  skipNote?: React.ReactNode;
  /** Called after a run with what actually happened. */
  onDeleted: (outcome: BulkDeleteOutcome) => void | Promise<void>;
}

export default function BulkDeleteControls({
  bulk, entityLabel, confirmWord, allowed, recoverable = false, skipNote, onDeleted,
}: BulkDeleteControlsProps) {
  // The setting being off means this office does not do this at all — nothing to show.
  if (!allowed) return null;

  const count = bulk.selectedIDs.length;
  // Distinguishes "not permitted" (hidden above) from "not yet possible" (shown, disabled).
  const waitingOnApi = !bulk.supported;

  return (
    <AccessControl roles={['ActsCareAdmin']}>
      <Group gap="xs" wrap="wrap">
        {!bulk.selecting ? (
          <Tooltip
            label={waitingOnApi
              ? `Deleting ${entityLabel} is not available in this API version yet`
              : `Select ${entityLabel} to delete`}
            withArrow
          >
            {/* Wrapped so the tooltip still fires while the button is disabled. */}
            <span>
              <Button
                variant="light"
                color="red"
                leftSection={<IconChecklist size={16} />}
                onClick={bulk.toggleSelecting}
                disabled={waitingOnApi}
              >
                Select to delete
              </Button>
            </span>
          </Tooltip>
        ) : (
          <>
            <Button
              variant="subtle"
              leftSection={<IconX size={16} />}
              onClick={bulk.toggleSelecting}
              disabled={bulk.busy}
            >
              Cancel selection
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={bulk.openConfirm}
              // ⚠️ Enabled only at one or more selected — the whole point of the two-stage flow.
              disabled={count === 0 || bulk.busy}
            >
              Delete{count > 0 ? ` ${count}` : ''}
            </Button>
            {count > 0 && (
              <Badge size="sm" variant="light" color="red">{count} selected</Badge>
            )}
          </>
        )}
      </Group>

      {/* Says what to do next, instead of leaving an empty toolbar and a dead button. */}
      {bulk.selecting && count === 0 && (
        <Alert color="blue" variant="light" icon={<IconAlertTriangle size={16} />} mt="sm">
          <Text size="sm">
            Tick the {entityLabel} you want to delete. The delete button turns on once at least one is
            selected.
          </Text>
        </Alert>
      )}

      <TypeToConfirmModal
        opened={bulk.confirmOpen}
        onClose={bulk.closeConfirm}
        title={`Delete ${count} ${count === 1 ? entityLabel.replace(/s$/, '') : entityLabel}`}
        confirmWord={confirmWord}
        count={count}
        busy={bulk.busy}
        actionLabel={`Delete ${count}`}
        warning={
          <>
            {recoverable
              ? <>These {entityLabel} disappear from the queue and from the dashboard totals, and there
                is no way to bring them back from this screen. The records are kept in the database, so
                ask the system team if something goes by mistake.</>
              : <>This cannot be undone. Once deleted these {entityLabel} cannot be retrieved, and
                anything already created from them will lose the link back.</>}
            {skipNote && <> {skipNote}</>}
          </>
        }
        onConfirm={async () => {
          const outcome = await bulk.run();
          // ⚠️ The failure is reported here rather than in each page, so all four queues say the same
          // thing about a 403 or an approved record.
          if (outcome.failure) toast.error(outcome.failure.title, outcome.failure.message);
          await onDeleted(outcome);
        }}
      />
    </AccessControl>
  );
}
